import { Injectable, Provider } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BehaviorSubject, catchError, EMPTY, filter, finalize, Observable, switchMap, take, throwError } from 'rxjs';
import { JwtService } from 'src/auth/services/jwt.service';
import { Router } from '@angular/router';
import { TokenRefreshResult } from 'src/shared/services/api.service';
import { AuthService } from '../services/auth.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  // would REALLY like to generate these via nswag but can't crack the code at the moment.
  private anonymousRoutes = [
    '/api/Admin/GetRoles',
    '/api/Auth/ExchangeIdpToken',
    '/api/Auth/CreateNonProdUserToken',
    '/api/Auth/ChangeEdOrg',
    '/api/Auth/GetEdOrgs',
    '/api/Auth/Logout',
    '/api/Auth/RefreshToken',
    '/api/Config/ClientConfig',
    '/api/Config/RenderView',
    '/api/Config/FeatureConfig',
    '/api/SurveyMonkey/oauth/callback',
    '/api/SurveyMonkey/webhook/completed-response',
    '/api/SurveyMonkey/webhook/updated-response',
    '/api/csp-report',
  ];

  constructor(
    private readonly jwtService: JwtService,
    private readonly router: Router,
    private readonly authService: AuthService,
  ) {}

  private addTokenHeader(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const token = this.jwtService.getToken();
    const isAnonymousRoute = this.anonymousRoutes.some((route) => request.url.startsWith(route));

    if (isAnonymousRoute || (!!token && !this.jwtService.isTokenExpired())) {
      request = this.addTokenHeader(request, this.jwtService.getToken()!);

      return next.handle(request).pipe(
        catchError((error) => {
          if (error.headers.has('clear-token')) {
            this.authService.logout();
            return EMPTY;
          }
          return throwError(() => error);
        }),
      );
    }

    return this.handleExpiredToken(request, next);
  }

  private handleExpiredToken(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap((result: TokenRefreshResult) => {
          if (result.wasRefreshed) {
            this.isRefreshing = false;
            this.refreshTokenSubject.next(result.token);
            return next.handle(this.addTokenHeader(request, result.token));
          } else {
            this.authService.logout();
            return EMPTY;
          }
        }),
        catchError((error) => {
          // On error, logout and clear the queue
          this.authService.logout();
          return EMPTY;
        }),
        finalize(() => {
          this.isRefreshing = false; // Reset the refreshing flag once the sequence completes or errors out
        }),
      );
    } else {
      return this.queueRequest(request, next);
    }
  }

  private queueRequest(request: HttpRequest<any>, next: HttpHandler) {
    return this.refreshTokenSubject.pipe(
      filter((token) => token != null),
      take(1),
      switchMap((jwt) => {
        return next.handle(this.addTokenHeader(request, jwt));
      }),
    );
  }
}

export const TokenInterceptorProvider: Provider = {
  provide: HTTP_INTERCEPTORS,
  useClass: TokenInterceptor,
  multi: true,
};
